/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeDeleteRequest, makeGetRequest, makePutRequest } from "../../network/http-client"
import type { ApplicationType } from "../application-type/application-type-types"

import type { UserAppTypeStore } from "./user-apptype-store"

enum Endpoint {
    user = "/api/private/user"
}

export class UserAppTypeService {
    constructor(
        private readonly store: UserAppTypeStore,
        private readonly logger: LoggerStore
    ) {}

    async getUserAppTypes(userId: number): Promise<Result<ApplicationType[]>> {
        const appTypes = await makeGetRequest<ApplicationType[]>(`${Endpoint.user}/${userId}/application-type`, {})
        if (appTypes.success && appTypes.data) {
            this.store.setUserAppTypesForUser(userId, appTypes.data)
        }
        if (!appTypes.success) {
            this.logger.logApiError("Get user app types failed", appTypes.apiError, appTypes.error)
        }
        return appTypes
    }

    async removeUserAppType(userId: number, appTypeId: number): Promise<Result<void>> {
        const res = await makeDeleteRequest<void>(`${Endpoint.user}/${userId}/application-type/${appTypeId}`, {})
        if (res.success) {
            this.logger.logOk("User app type removed successfully")
        }
        if (!res.success) {
            this.logger.logApiError("Remove user app type failed", res.apiError, res.error)
        }
        return res
    }

    async removeUserAppTypes(userId: number, appTypeIds: number[]): Promise<Result<void>> {
        const results = await Promise.all(appTypeIds.map((appTypeId) => this.removeUserAppType(userId, appTypeId)))

        const failed = results.find((r) => !r.success)
        if (failed) {
            this.logger.logApiError("Remove user app types failed", failed.apiError, failed.error)
            return { success: false, error: new Error("Not all app types were removed successfully.") }
        }

        return { success: true, data: null }
    }

    async assignUserAppType(userId: number, appTypeId: number): Promise<Result<void>> {
        const res = await makePutRequest<never, void>(`${Endpoint.user}/${userId}/application-type/${appTypeId}`, {})
        if (res.success) {
            this.logger.logOk("User was assigned to application type")
        }
        if (!res.success) {
            this.logger.logApiError("Assign user to application type failed", res.apiError, res.error)
        }
        return res
    }

    async assignUserAppTypes(userId: number, appTypeIds: number[]): Promise<Result<void>> {
        const results = await Promise.all(appTypeIds.map((id) => this.assignUserAppType(userId, id)))

        const failed = results.find((r) => !r.success)
        if (failed) {
            this.logger.logApiError("Assign user to application type failed", failed.apiError, failed.error)
            return { success: false, error: new Error("Not all app types were assigned successfully.") }
        }

        return { success: true, data: undefined }
    }
}
